/** The RDF "kind" a term is classified as, for iconography/coloring/filtering. */
export type TermKind =
  | "Class"
  | "ObjectProperty"
  | "DatatypeProperty"
  | "AnnotationProperty"
  | "Property"
  | "NodeShape"
  | "PropertyShape"
  | "Ontology"
  | "Datatype"
  | "Individual";

/** Lightweight summary used everywhere a term is listed (tree nav, search results, hop graph nodes). */
export interface TermSummary {
  iri: string;
  prefixedName: string;
  label: string | null;
  comment: string | null;
  kind: TermKind;
  /** Dataset id (from the registry) that declares this term, for provenance/coloring. */
  datasetId: string;
}

/** One row in a term's property table (plain predicate/object facts, not SHACL constraints). */
export interface PropertyRow {
  predicateIri: string;
  predicatePrefixed: string;
  predicateLabel: string | null;
  /** Human-readable rendering of the object (compacted IRI, literal value, or literal + language/datatype). */
  value: string;
  valueIri: string | null;
}

/** A single `sh:property` constraint attached to a NodeShape. */
export interface PropertyShapeConstraint {
  pathIri: string | null;
  pathPrefixed: string | null;
  name: string | null;
  description: string | null;
  classIri: string | null;
  classPrefixed: string | null;
  datatypeIri: string | null;
  datatypePrefixed: string | null;
  minCount: number | null;
  maxCount: number | null;
  pattern: string | null;
  nodeIri: string | null;
}

/** SHACL shape info for a term, when it is (or has) a NodeShape. */
export interface ShapeInfo {
  shapeIri: string;
  shapePrefixed: string;
  targetClassIris: string[];
  properties: PropertyShapeConstraint[];
}

/** Full detail view model for a single term, as rendered by the Explorer's detail panel. */
export interface TermDetail extends TermSummary {
  typeIris: string[];
  superClasses: TermSummary[];
  subClasses: TermSummary[];
  superProperties: TermSummary[];
  subProperties: TermSummary[];
  domain: TermSummary[];
  range: TermSummary[];
  equivalentClasses: TermSummary[];
  properties: PropertyRow[];
  shapes: ShapeInfo[];
  /** NodeShapes elsewhere in the store that target this term as a class. */
  targetedByShapes: ShapeInfo[];
  /** Named individuals whose `rdf:type` is this term — populated for Class-like terms. */
  instances: TermSummary[];
}

/** One source file backing a dataset version, shown in the Files tab. */
export interface DatasetFile {
  id: string;
  fileName: string;
  /** Fetch path relative to the app base — ignored when `text` is present. */
  path: string;
  format: "turtle" | "ntriples" | "trig" | "nquads";
  /** Inline file content for user-uploaded datasets (no fetch round-trip). */
  text?: string;
}

/** One released/published version of an ontology. */
export interface OntologyVersion {
  /** Stable version id used in selection state, e.g. "3.1.1". */
  id: string;
  /** Display label, e.g. "v3.1.1". */
  label: string;
  /** ISO date the version was released/snapshotted, when known. */
  releaseDate?: string;
  /** Free-form note, e.g. "current W3C Recommendation". */
  note?: string;
  files: DatasetFile[];
}

/** An ontology/vocabulary and its metadata, driving the switcher + Files/Ontologies tabs. */
export interface DatasetDescriptor {
  id: string;
  label: string;
  shortLabel: string;
  description: string;
  namespaceIri: string;
  sourceUrl: string;
  license: string;
  /** Accent color (CSS color) used for this dataset's nodes/badges throughout the app. */
  accentColor: string;
  /** Where the dataset came from — bundled snapshots vs. a user upload. */
  origin: "bundled" | "user";
  /** Available versions, newest first. Exactly one is loaded at a time. */
  versions: OntologyVersion[];
  /** Default on/off state in the dataset switcher. */
  enabledByDefault: boolean;
}

/** Picks the version of `dataset` selected in `selections`, falling back to the newest. */
export function resolveVersion(
  dataset: DatasetDescriptor,
  selections: ReadonlyMap<string, string> | null | undefined,
): OntologyVersion {
  const selectedId = selections?.get(dataset.id);
  return (
    dataset.versions.find((v) => v.id === selectedId) ?? dataset.versions[0]!
  );
}

/** A node in a hop-BFS result, annotated with its distance from the focus term. */
export interface HopNode extends TermSummary {
  hopDistance: number;
}

/** An edge in a hop-BFS result. */
export interface HopEdge {
  id: string;
  sourceIri: string;
  targetIri: string;
  predicateIri: string;
  predicatePrefixed: string;
  /** min(hopDistance of the two endpoints) — used for edge fade-by-distance styling. */
  hopDistance: number;
}

export interface HopGraph {
  focusIri: string;
  nodes: HopNode[];
  edges: HopEdge[];
}

/** Which structural relations the hop BFS traverses — user-togglable in the Visualizer. */
export interface HopEdgeTypeFilter {
  subClassOf: boolean;
  subPropertyOf: boolean;
  domainRange: boolean;
  shaclShapes: boolean;
  equivalentClass: boolean;
  rdfType: boolean;
}

export const DEFAULT_HOP_EDGE_FILTER: HopEdgeTypeFilter = {
  subClassOf: true,
  subPropertyOf: true,
  domainRange: true,
  shaclShapes: true,
  equivalentClass: true,
  rdfType: false,
};
